import { useEffect, useState } from 'react';
import { isValidVin, isValidWorkOrder } from '@/utils';
import { Box, Typography } from '@mui/material';
import MissingItemsDialog from './MissingItemsDialog';
import { useTranslation } from 'react-i18next';
import { Asset } from '@/types/asset_search';

export type MissingItemsProps = {
  search: string;
  items: any[];
};

export default function MissingItemsLabel({
  search,
  items = [],
}: MissingItemsProps) {
  const { t } = useTranslation();

  const [foundItems, setFoundItems] = useState<Asset[]>([]);
  const [missingItems, setMissingItems] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const toggleMissingItemsDialog = () => {
    setDialogOpen((prev) => !prev);
  };

  useEffect(() => {
    if (items.length === 0) return;

    // Normalize the search value by splitting by comma or space and removing any extra spaces
    const searchTerms = Array.from(
      new Set(
        search
          .toLowerCase()
          .split(/[\s,]+/) // Split by commas or spaces
          .filter((term) => term.trim() !== ''),
      ),
    );

    const missingItems: string[] = [];

    // Determine missing items based on validity and absence in items array
    searchTerms.forEach((term) => {
      const found = items.some(
        (item) =>
          (item.asset_uuid && item.asset_uuid.toLowerCase().includes(term)) ||
          (item.work_order && item.work_order.toLowerCase().includes(term)),
      );

      // Only add to missingItems if it is a valid VIN or Work Order and wasn't found
      if (!found && (isValidVin(term) || isValidWorkOrder(term))) {
        missingItems.push(term);
      }
    });

    setMissingItems(missingItems);
    setFoundItems(items); // All items are considered found
  }, [items, search]);

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {items.length !== 0 && (
        <Box
          sx={{
            width: '100%',
            alignItems: 'left',
            display: 'flex',
            justifyContent: 'left',
          }}
        >
          <Typography
            textAlign="left"
            fontSize="16px"
            fontWeight="600"
            data-testid="label-found-items"
            sx={(theme) => ({
              color: theme.palette.text.primary,
            })}
          >
            {foundItems.length} {t('missingItemsDialogs.itemsFound')}
          </Typography>
          {missingItems.length !== 0 && items.length !== 0 && (
            <Typography
              textAlign="left"
              fontSize="16px"
              fontWeight="600"
              data-testid="label-missing-items"
              color="#0070BF"
              paddingLeft="4px"
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              onClick={toggleMissingItemsDialog}
            >
              {missingItems.length} {t('missingItemsDialogs.missing')}
            </Typography>
          )}
        </Box>
      )}
      <MissingItemsDialog
        open={dialogOpen}
        missingItems={missingItems}
        setOpen={setDialogOpen}
      />
    </Box>
  );
}
